import { useEffect, useState } from 'react'
import { trackEvent } from '../../lib/analytics'
import { appStoreUrl } from '../../lib/appStore'
import { playStoreUrl } from '../../lib/playStore'
import {
  installPromptPlatform,
  readUaEnv,
  IOS_PROMPT_DISMISS_KEY,
  readRecordedIosBannerDecision,
  type InstallPromptPlatform,
} from '../../lib/iosDetect'
import IosInstallBanner from './IosInstallBanner'
import { useCommonT } from '../../i18n'

// Shared with the prerender's inline banner-slot script (see iosDetect.ts),
// and shared by both platforms: one dismissal hides the banner for good.
const DISMISS_KEY = IOS_PROMPT_DISMISS_KEY
const IOS_CAMPAIGN = 'web_ios_autoprompt'
const ANDROID_CAMPAIGN = 'web_android_autoprompt'

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function rememberDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, '1')
  } catch {
    // ignore (private mode)
  }
}

/**
 * Global, dismissible install banner rendered at the top of page content.
 * This is the SINGLE iOS install mechanism (we don't use Apple's native Smart
 * App Banner - see src/lib/iosDetect.ts). It shows on iOS in EVERY browser,
 * including the Facebook / Instagram / Reddit in-app webviews, which are the
 * highest-value placements (pushed deck links open there and the native banner
 * never renders). On an Android browser the same banner, in the same slot,
 * offers Google Play instead (installPromptPlatform). Never inside our Android
 * app. Dismissal persists in localStorage, one key for both.
 */
export default function IosInstallPrompt() {
  // Compute eligibility once at mount (client-only SPA; readUaEnv/wasDismissed
  // guard for missing navigator/localStorage). Lazy initializer avoids a
  // setState-in-effect cascade.
  const t = useCommonT()
  // The platform decides the copy and the store, never whether to show on a
  // prerendered page: there the inline script before #root already decided,
  // and reserved (or did not reserve) the banner's space on that decision.
  // Follow it, so the banner lands exactly in that space. Everywhere else,
  // decide here as always.
  const [platform] = useState<InstallPromptPlatform | null>(() => installPromptPlatform(readUaEnv()))
  const [visible, setVisible] = useState(
    () => readRecordedIosBannerDecision() ?? (platform !== null && !wasDismissed()),
  )
  const android = platform === 'android'
  // eslint-disable-next-line i18next/no-literal-string -- analytics prop value
  const store = android ? 'android' : 'ios'
  const campaign = android ? ANDROID_CAMPAIGN : IOS_CAMPAIGN

  useEffect(() => {
    if (visible) {
      trackEvent('ios_cta_shown', { placement: campaign, surface: 'auto_banner', platform: store })
    }
    // Fire the shown event exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!visible) return null

  return (
    <IosInstallBanner
      title={android ? t('android_prompt.title') : t('ios_prompt.title')}
      body={android ? t('android_prompt.body') : t('ios_prompt.body')}
      getLabel={android ? t('android_prompt.get') : t('ios_prompt.get')}
      dismissLabel={t('ios_prompt.dismiss')}
      href={android ? playStoreUrl(campaign) : appStoreUrl(campaign)}
      // eslint-disable-next-line i18next/no-literal-string -- asset path
      iconSrc={android ? '/icon-192.png' : '/app-icon.png'}
      onGet={() => trackEvent('ios_cta_clicked', { placement: campaign, platform: store })}
      onDismiss={() => {
        rememberDismissed()
        setVisible(false)
      }}
    />
  )
}
